import { LinkField } from '@sitecore-jss/sitecore-jss-react';
import { Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import Button from 'components/helpers/Button';
import { classnames } from 'tailwindcss-classnames';
import { useCheckout } from 'components/helpers/Cart/CheckoutHelper';
import AlertSuccess from 'components/helpers/SvgIcon/icons/alert-success';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useDictionary from 'lib/sitecore/use-dictionary';
import { OrderDataProps } from './MailOrder';
import { useCart } from 'components/helpers/Cart/CartHelper';
import useSafeSession from 'lib/util/useSafeSession';

type CheckoutReviewProps = {
  updateSteps: (value: number) => void;
  updateCustomerData: (orderData: OrderDataProps) => void;
  placeOrderCTA: LinkField;
  reviewLabel: Field<string>;
  shippingAddressLabel: Field<string>;
  shippingMethodLabel: Field<string>;
  placeOrderTC: Field<string>;
};

const CheckoutReview = ({
  placeOrderCTA,
  reviewLabel,
  shippingAddressLabel,
  shippingMethodLabel,
  placeOrderTC,
  updateSteps,
  updateCustomerData,
}: CheckoutReviewProps): JSX.Element => {
  const { checkoutData } = useCheckout();
  const { cart } = useCart();
  const dictionary = useDictionary();
  const textSolidBlack = 'text-solidBlack';
  const progressTitle = 'progress-title';
  const fontMedium = 'font-medium';
  const { data: session } = useSafeSession();
  const userEmail = session?.user?.email || '';
  const user = userEmail !== '' || userEmail !== undefined ? userEmail.split('@')[0] : '';
  const addressInformation = checkoutData.checkOutData.addressData;
  const ShippingData = checkoutData.checkOutData.shippingMethod;
  const orderData: OrderDataProps = {
    FirstName: addressInformation.firstName,
    LastName: addressInformation.lastName,
    Company: addressInformation.company,
    Address: addressInformation.address,
    City: addressInformation.city,
    State: addressInformation.state,
    Zip: addressInformation.zipCode,
    Country: 'United States',
    Phone: addressInformation.phone,
    Email: addressInformation.email,
    User: user,
    ShippingMethod: ShippingData.shippingMethod,
    FedExNumber: ShippingData.fedExNumber,
    FedExRushMethod: ShippingData.deliverySelection,
    OrderedProducts: cart,
  };

  return (
    <>
      <div className={classnames('checkout-form', 'justify-center', 'w-full', textSolidBlack)}>
        <div className={classnames('md:hidden')}>
          <div className={classnames(progressTitle, 'complete', 'pb-8', textSolidBlack)}>
            <AlertSuccess />
            {shippingAddressLabel.value}
          </div>
          <div className={classnames(progressTitle, 'complete', 'pb-8', textSolidBlack)}>
            <AlertSuccess />
            {shippingMethodLabel.value}
          </div>
        </div>
        <form>
          <div className={classnames('review-form', 'flex', 'flex-wrap')}>
            <div>
              <Text
                className={classnames(
                  progressTitle,
                  'text-body',
                  textSolidBlack,
                  fontMedium,
                  'active',
                  'ml-1.5',
                  'pl-6',
                  'md:ml-0',
                  'md:p-0',
                  'md:text-2xl'
                )}
                field={reviewLabel}
                tag="h2"
              />
            </div>
          </div>
          <div className={classnames('pl-7', 'pt-4', 'md:pl-0')}>
            <div
              className={classnames(
                'flex',
                'pb-2.5',
                'md:pb-4',
                'justify-between',
                'sm:justify-start'
              )}
            >
              <h3 className={classnames('text-body', fontMedium, textSolidBlack)}>
                {shippingAddressLabel.value}
              </h3>
              <Button
                className={classnames('font-regular', 'pl-6')}
                onClick={() => updateSteps(0)}
                text="Edit"
                variant="link"
              />
            </div>
            <div className={classnames('leading-4', 'flex')}>
              <span>{checkoutData.checkOutData.addressData.firstName}</span>
              <div className={classnames('pl-1')}>
                {checkoutData.checkOutData.addressData.lastName}
              </div>
            </div>
            <address>
              {checkoutData.checkOutData.addressData.company}
              <br />
              {checkoutData.checkOutData.addressData.address}
              {checkoutData.checkOutData.addressData.address2 !== '' ? ', ' : ' '}
              {checkoutData.checkOutData.addressData.address2}
              <br />
              <span> {checkoutData.checkOutData.addressData.city}</span>
              {', '}
              <span> {checkoutData.checkOutData.addressData.state}</span>
              <span> {checkoutData.checkOutData.addressData.zipCode}</span>
            </address>
          </div>
          <div className={classnames('pl-7', 'pt-4', 'md:pl-0')}>
            <h3
              className={classnames('pb-2.5', 'text-body', fontMedium, textSolidBlack, 'md:pb-4')}
            >
              {dictionary.checkout_contact_info_label}
            </h3>
            <div>
              <div>
                {dictionary.checkout_email}
                {': '} {checkoutData.checkOutData.addressData.email}
              </div>
              <div>
                {dictionary.checkout_phone}
                {': '} {checkoutData.checkOutData.addressData.phone}
              </div>
            </div>
          </div>
          <div className={classnames('pl-7', 'pt-4', 'md:pl-0')}>
            <div
              className={classnames(
                'flex',
                'pb-2.5',
                'justify-between',
                'sm:justify-start',
                'md:pb-4'
              )}
            >
              <h3 className={classnames('text-body', fontMedium, textSolidBlack)}>
                {shippingMethodLabel.value}
              </h3>
              <Button
                className={classnames('font-regular', 'pl-6')}
                onClick={() => updateSteps(1)}
                text="Edit"
                variant="link"
              />
            </div>
            <div>{checkoutData.checkOutData.shippingMethod.shippingMethod}</div>
            {checkoutData.checkOutData.shippingMethod.fedExNumber !== '' && (
              <>
                <div>{checkoutData.checkOutData.shippingMethod.deliverySelection}</div>
                <div>{checkoutData.checkOutData.shippingMethod.fedExNumber}</div>
              </>
            )}
          </div>
          <div className={classnames('pl-7', 'pt-10', 'md:pl-0')}>
            <Button
              className={classnames('w-full', 'text-center', 'md:w-auto')}
              linkField={placeOrderCTA}
              onClick={() => {
                updateSteps(3);
                updateCustomerData(orderData);
              }}
            />
          </div>
        </form>
        <div className={classnames('rte', 'pl-7', 'pt-6', 'md:w-1/2', 'md:pl-0')}>
          <RichTextA11yWrapper field={placeOrderTC} />
        </div>
      </div>
    </>
  );
};

export default CheckoutReview;
